import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import {
  MealCatalogItem,
  PassengerApplicationStatusMap,
  SegmentMap,
} from '@common/interfaces/mybooking/flight-meal.interface';
import {
  AppliedMealList,
  FlightMealInfo,
  Meal,
  TravelerMealInfo,
} from '@common/interfaces/mybooking/order-services.interface';
import { PassengerType } from '@common/interfaces/mybooking/passenger-type';
import { FlightMealService } from '@common/services/my-booking/flight-meal-service/flight-meal-service';
import { SupportComponent } from '@lib/components/support-class/support-component';
import { ModalService } from '@lib/services';
import { CommonLibService } from '@lib/services/common-lib/common-lib.service';
import { Subscription } from 'rxjs';
import {
  GetMealResponseData,
  GetMealResponseDataDD,
  GetMealResponseDataDDAppliedMealListInner,
  GetOrderResponseData,
} from 'src/sdk-servicing';
import { GetOrderResponseDataOrderType } from 'src/sdk-servicing/model/getOrderResponseDataOrderType';
import { OrderSeatItemSeatSelection } from 'src/sdk-servicing/model/orderSeatItemSeatSelection';
import { Traveler } from 'src/sdk-servicing/model/traveler';
import { Type1 } from 'src/sdk-servicing/model/type1';
import { selectMenuPayloadParts } from '../../select-menu/select-menu-payload.state';
import { isEmptyArray, isEmptyObject } from '@common/helpers';
import { StaticMsgPipe } from '@lib/pipes';
import { isStringEmpty } from '@common/helpers';
import { v4 as uuidV4 } from 'uuid';
import { IN_FLIGHT_MEAL_TYPE } from '@common/helpers/constants/my-booking/flight_meal_request.constant';

@Component({
  selector: 'asw-flight-meal-detail',
  templateUrl: './flight-meal-detail.component.html',
  styleUrls: ['./flight-meal-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightMealDetailComponent extends SupportComponent {
  /** 当該セグメント情報 */
  @Input() flight!: Type1;
  @Input() traveler!: Traveler;
  @Input() isNotAppliedMeal: boolean = false;
  @Input() isBabyMeal: boolean = false;
  @Input() appliedMeal!: AppliedMealList;

  inFlightMealName: string = '';
  labelInFlightMealName: string = '';
  isDisplaySelectedMealIcon: boolean = false;
  unableToClickSelectMeal: boolean = false;
  serviceStatus: string = '';
  isDisplayMealStatus: boolean = false;
  isBabyMealChecked: boolean = false;
  isDisabledCheckBox: boolean = false;
  isHighlightBorder: boolean = false;
  inFlightMealType!: string;
  travelerStatusOfMeal!: PassengerApplicationStatusMap;
  getMealByFlight!: GetMealResponseDataDD;
  /** [1.2.13] 削除ボタン */
  isDeleteBtnDisplay: boolean = false;
  /** [1.2.14] サービス金額 */
  serviceAmount: number = 0;
  babyMealStatus!: string;
  babyMealLabel!: string;
  isDisplayBabyMealCheckBox = false;
  _subscriptions: Subscription = new Subscription();
  pnr!: GetOrderResponseData;
  meal!: GetMealResponseData;

  uuid: string = uuidV4();

  constructor(
    private _common: CommonLibService,
    private _flightMealService: FlightMealService,
    private _change: ChangeDetectorRef,
    private _modal: ModalService,
    private _staticMsgPipe: StaticMsgPipe
  ) {
    super(_common);
  }
  init(): void {
    this._subscriptions.add(
      this._flightMealService.inFlightMealRequestData$.subscribe((data) => {
        this.pnr = data.pnr ?? ({} as GetOrderResponseData);
        this.meal = data.meal ?? ({} as GetMealResponseData);

        this.getMealByFlight = this.meal?.[this.traveler.id!]?.[this.flight.id!] ?? ({} as GetMealResponseDataDD);
        this.inItMealInFlight();
        this._change.markForCheck();
      })
    );

    // Rerender when select another meal in Select-Menu modal
    this._subscriptions.add(
      this._flightMealService._reRender.subscribe((reRender) => {
        if (reRender) {
          this.inItMealInFlight();
          this._change.markForCheck();
        }
      })
    );
  }

  /**
   * Render all information in meal
   */
  inItMealInFlight() {
    this.travelerStatusOfMeal = this._flightMealService.mealInformation.segmentMap
      ?.get(this.flight.id!)
      ?.passengerApplicationStatusMap.get(this.traveler.id!)!;

    if (this.isBabyMeal !== true) {
      // [1.2.12] 機内食名称

      this.inFlightMealName = this.getInFlightMealName();

      // Save info to handle Event number 10 at 5.3
      this.setCanSelectAllMealByTravelers();

      // [1.2.9] 機内食選択
      this.unableToClickSelectMeal = this.inFlightMealStatus();

      if (this.isNotAppliedMeal !== true || this._flightMealService._reRender.value) {
        if (this.isNotAppliedMeal !== true) {
          // [1.2.10] 申込状態
          this.serviceStatus = this.setServiceStatus(this.appliedMeal?.serviceStatus!);
          // Save info to handle Event number 10 at 5.3
          this.setCanSelectAllMealByTravelers();
        }

        // [1.2.11] 機内食種別
        this.inFlightMealType = this.getInFlightMealType();

        // [1.2.13] 削除ボタン
        this.displayDeleteBtn();

        // [1.2.14] サービス金額
        this.serviceAmount = this.mealAmount();
      }
    } else {
      // [1.2.15] ベビーミール選択
      this.babyMealStatus = this.getBabyMealStatus();
      // ※当項目の名称はSSRコード=""BBML""を引数として、「当画面共通処理No4.特別機内食名称取得処理」を行い、返却値の特別機内食名称とする。"
      this.babyMealLabel = this._flightMealService.onHandleCommonNo4(TravelerMealInfo.MealCode.BBML);
      this.isDeleteBtnDisplay = false;
      this.unableToClickSelectMeal = false;
    }
  }

  /**
   * [1.2.15] ベビーミール選択
   * @returns
   */
  getBabyMealStatus() {
    /** 申込済機内食情報 */
    const inFlightMealInforApply =
      (((this.pnr?.services?.meal as Meal)?.[this.flight.id!] as FlightMealInfo)?.[
        this.traveler.id!
      ] as TravelerMealInfo) ?? {};
    let babyMealFlag: boolean =
      inFlightMealInforApply?.appliedMealList?.some(
        (appliedMeal: AppliedMealList) => appliedMeal.code === TravelerMealInfo.MealCode.BBML
      ) ?? false;
    // [1.2.15.2] 上記以外の場合、非表示する。
    let babyMealStatus: string = 'hidden';

    const accompanyingTravelerMealInfoByFlight =
      this.meal?.[this.traveler.accompanyingTravelerId!]?.[this.flight.id!] ?? ({} as GetMealResponseDataDD);

    const ssrCodeNotBBMLCount = accompanyingTravelerMealInfoByFlight?.appliedMealList?.reduce(
      (acc: number, curr: AppliedMealList) => {
        if (curr?.code !== TravelerMealInfo.MealCode.BBML) {
          return acc + 1;
        }
        return acc;
      },
      0
    );

    // [1.2.15.1] 当該搭乗者情報.passengerTypeCode=""INF""(席なし幼児)の場合、以下のケースのうち 1 つを表示
    if (this.traveler.passengerTypeCode === PassengerType.INF) {
      // [1.2.15.1.1] 当該セグメント情報.isJapanDomesticFlight=true(日本国内線)の場合、提供対象外の旨を表示し、チェックボックスは非表示とする。
      if (this.flight.isJapanDomesticFlight) {
        // 提供対象外の旨
        babyMealStatus = 'label.uavailableMeal';
        this.isDisplayBabyMealCheckBox = false;
      }
      // [1.2.15.1.2] 当該セグメント別機内食情報.appliedMealListの中に、SSRコード=""BBML""がない、かつ当該セグメント別機内食情報.isWithinApplicationDeadlineがfalse(申込期限外)の場合、受付終了である旨を表示し、チェックボックスは非表示とする。
      //受付終了である旨
      else if (!babyMealFlag && this.getMealByFlight.isWithinApplicationDeadline !== true) {
        babyMealStatus = 'label.receptionClosed';
        this.isDisplayBabyMealCheckBox = false;
      } else if (
        // [1.2.15.1.3] 当該セグメント別機内食情報.isWithinApplicationDeadlineがtrue、かつ以下のいずれかに該当する場合、選択済みかつ、当項目の名称を表示する。
        this.getMealByFlight.isWithinApplicationDeadline === true && // [1.2.15.1.3.1] 更新サービス情報の値が以下条件をすべて満たす
        // [1.2.15.1.3.1.1] 更新サービス情報.セグメントマップ.<当該セグメント情報.Id>.搭乗者別申込状況マップ.<当該搭乗者情報.id>が存在する
        ((this.travelerStatusOfMeal &&
          // [1.2.15.1.3.1.2] 当該搭乗者別申込状況マップ.<当該搭乗者情報.id>.ベビーミール選択フラグ=true(選択中)
          this.travelerStatusOfMeal?.babyMealSelectionFlag === true) ||
          // [1.2.15.1.3.2] 申込済機内食情報.appliedMealListの中に、SSRコード=""BBML""が存在する
          babyMealFlag)
      ) {
        // [1.2.15.1.3] 以下のいずれかに該当する場合、選択済みかつ、当項目の名称を表示する。
        this.isBabyMealChecked = true;
        this.isDisplayBabyMealCheckBox = true;
        // ※ネイビーで枠囲いする。
        this.isHighlightBorder = true;
        // border-color: blue
      } else if (
        // [1.2.15.1.4] 当該セグメント別機内食情報.isWithinApplicationDeadlineがfalse、かつ以下のいずれかに該当する場合、非活性とする。
        this.getMealByFlight.isWithinApplicationDeadline !== true &&
        // [1.2.15.1.4.1] 更新サービス情報の値が以下条件をすべて満たす
        // [1.2.15.1.4.1.1] 更新サービス情報.セグメントマップ.<当該セグメント情報.Id>.搭乗者別申込状況マップ.<当該搭乗者情報.id>が存在する
        ((this.travelerStatusOfMeal &&
          // [1.2.15.1.4.1.2] 当該搭乗者別申込状況マップ.<当該搭乗者情報.id>.ベビーミール選択フラグ=true(選択中)
          this.travelerStatusOfMeal.babyMealSelectionFlag === true) ||
          // [1.2.15.1.4.2] 申込済機内食情報.appliedMealListの中に、SSRコード=""BBML""が存在する
          babyMealFlag)
      ) {
        this.isBabyMealChecked = true;
        this.isDisplayBabyMealCheckBox = true;
        this.isDisabledCheckBox = true;
      }
      // [1.2.15.1.5] 当該搭乗者情報.accompanyingTravelerId≠“”(空欄)(同伴搭乗者がいる)、かつ同伴者機内食情報が存在する、かつ同伴者機内食情報.appliedMealListの中に、code≠""BBML""(ベビーミール)の件数 ≧ 2件の場合、非活性とする。
      else if (
        !isStringEmpty(this.traveler.accompanyingTravelerId) &&
        !isEmptyObject(accompanyingTravelerMealInfoByFlight) &&
        ssrCodeNotBBMLCount >= 2
      ) {
        this.isBabyMealChecked = true;
        this.isDisplayBabyMealCheckBox = true;
        this.isDisabledCheckBox = true;
      }
      // [1.2.15.1.6] 上記以外の場合、未選択かつ、当項目の名称を表示する。
      else {
        this.isBabyMealChecked = false;
        this.isDisplayBabyMealCheckBox = true;
      }
    }

    return babyMealStatus;
  }

  /** [1.2.14] サービス金額 */
  mealAmount() {
    let serviceAmount = 0;
    // [1.2.14.1] 更新サービス情報.セグメントマップ.<当該セグメント情報.id>.搭乗者別申込状況マップ.<当該搭乗者情報.id>が存在する場合、有料サービス金額を表示する。
    if (this.travelerStatusOfMeal) {
      serviceAmount = this.travelerStatusOfMeal?.paidServiceAmount ?? 0;
    } else if (this.appliedMeal?.price?.total) {
      // [1.2.14.3] 当該申込済情報が存在する場合、当該申込済情報.price.totalを表示する。
      serviceAmount = this.appliedMeal.price.total;
    }
    return serviceAmount;
  }

  /**
   * [1.2.13.1] 以下のいずれかの場合、表示する。
   */
  displayDeleteBtn() {
    // 1.以下のいずれかの場合、表示する。
    // 1.1.更新サービス情報.セグメントマップ.<当該セグメント情報.Id>.搭乗者別申込状況マップ.<当該搭乗者情報.Id>.更新区分=“request”(登録)が存在する
    if (
      this.travelerStatusOfMeal &&
      this.travelerStatusOfMeal.updateCategory === TravelerMealInfo.UpdateCategory.REQUEST
    ) {
      this.isDeleteBtnDisplay = true;
      return;
    }

    // 1.2.更新サービス情報.セグメントマップ.<当該セグメント情報.Id>.搭乗者別申込状況マップ.<当該搭乗者情報.Id>が存在しない場合
    if (!this.travelerStatusOfMeal) {
      if (
        this.appliedMeal?.serviceStatus === OrderSeatItemSeatSelection.ServiceStatusEnum.Confirmed || // 1.2.1 当該申込済情報.serviceStatus=""confirmed""
        (this.appliedMeal?.serviceStatus === OrderSeatItemSeatSelection.ServiceStatusEnum.ConfirmedWithoutTsm &&
          this.pnr?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh) // 1.2.2 当該申込済情報.serviceStatus=""confirmedWithoutTsm""かつ、PNR情報取得API応答.data.orderType.channelType=""nh""(直営PNR)
      ) {
        this.isDeleteBtnDisplay = true;
        return;
      }
    }

    // 2.上記以外の場合、非表示ボタンに対する読み上げ用ラベルは外部設計書に従い出力する。
    this.isDeleteBtnDisplay = false;
  }

  /**
   * [1.2.11] 以下のいずれかの場合、機内食種別を非表示する。
   * @returns
   */
  getInFlightMealType() {
    let inFlightMealType = '';

    // [1.2.11.1] 更新サービス情報.セグメントマップ.<当該セグメント情報.Id>.搭乗者別申込状況マップ.<当該搭乗者情報.id>.機内食種別が存在する場
    if (this.travelerStatusOfMeal?.mealType) {
      inFlightMealType = this.travelerStatusOfMeal?.mealType;
    }
    // [1.2.11.2] 上記以外の場合、当該申込済情報.typeに応じて機内食種別の名称を表示する。
    else if (this.appliedMeal?.type) {
      inFlightMealType = this.appliedMeal?.type;
    }
    // [1.2.11.3] 以下のいずれかの場合、機内食種別を非表示する
    else if (
      this.flight.isJapanDomesticFlight || // [1.2.11.3.1] 当該セグメント情報.isJapanDomesticFlight=true(日本国内線)
      this.labelInFlightMealName === 'label.normalMeal' || // [1.2.11.3.2] No12の機内食名称が通常機内食の旨
      this.labelInFlightMealName === 'label.receptionClosed' || // [1.2.11.3.3] No12の機内食名称が受付終了である旨
      this.labelInFlightMealName === 'label.unknownMenu' || // [1.2.11.3.4] No12の機内食名称が機内食の事前リクエストをしない旨
      this.labelInFlightMealName === 'label.decliningInflightMeals' // [1.2.11.3.5] No12の機内食名称が機内食を辞退している旨
    ) {
      inFlightMealType = '';
    } else {
      inFlightMealType = this.appliedMeal?.type ?? '';
    }
    return IN_FLIGHT_MEAL_TYPE[inFlightMealType]
      ? this._staticMsgPipe.transform(IN_FLIGHT_MEAL_TYPE[inFlightMealType])
      : '';
  }

  /** [1.2.12] 機内食名称 */
  getInFlightMealName() {
    this.travelerStatusOfMeal = this._flightMealService.mealInformation.segmentMap
      ?.get(this.flight.id!)
      ?.passengerApplicationStatusMap.get(this.traveler.id!)!;

    let labelInFlightMealName = '';
    // [1.2.12.1] 当該セグメント情報.isJapanDomesticFlight=true(日本国内線)の場合、以下、利用可能なお食事・お飲み物の内容に応じた文言を表示する。
    if (this.flight.isJapanDomesticFlight) {
      labelInFlightMealName = this.setInFlightMealName(this.flight?.meal?.mealCode);
    } else {
      // [1.2.12.2] 上記以外の場合、以下のケースのうち 1 つを表示
      // [1.2.12.2.1] 更新サービス情報.セグメントマップ.<当該セグメント情報.Id>.搭乗者別申込状況マップ.<当該搭乗者情報.id>が存在する場合、以下のケースのうち 1 つを表示
      if (this.travelerStatusOfMeal) {
        // [1.2.12.2.1.1] 更新サービス情報.セグメントマップ.<当該セグメント情報.Id>.搭乗者別申込状況マップ.<当該搭乗者情報.Id>.更新区分=“cancel”(削除)が存在する場合、
        if (this.travelerStatusOfMeal?.updateCategory === TravelerMealInfo.UpdateCategory.CANCEL) {
          // [1.2.12.2.1.1.1] 当該搭乗者情報.passengerTypeCode≠"INS"(席あり幼児)、かつ 当該セグメント別機内食情報.preorderMealListが存在する場合、機内食の事前リクエストをしない旨を表示する。
          if (
            this.traveler?.passengerTypeCode !== PassengerType.INS &&
            !isEmptyArray(this.getMealByFlight?.preorderMealList)
          ) {
            return this._staticMsgPipe.transform('label.normalMeal');
          }
          // [1.2.12.2.1.1.2] 上記以外の場合、通常機内食の旨を表示する。
          else {
            return this._staticMsgPipe.transform('label.in-flightMeal');
          }
        }
        // [1.2.12.2.1.2] 当該搭乗者別申込状況マップ.SSRコード=""NTML""の場合、機内食を辞退している旨を表示する。
        if (this.travelerStatusOfMeal?.ssrCode === 'NTML') {
          labelInFlightMealName = 'label.decliningInflightMeals';
          return this._staticMsgPipe.transform(labelInFlightMealName);
        }
        // [1.2.12.2.1.3] 当該搭乗者別申込状況マップ.事前ミールメッセージIDが存在する場合、当該搭乗者別申込状況マップ.事前ミールメッセージIDに応じた事前オーダーミール名称を表示する。
        if (this.travelerStatusOfMeal?.mealMessageId) {
          labelInFlightMealName = this.travelerStatusOfMeal?.mealMessageId;
          return this._staticMsgPipe.transform(labelInFlightMealName);
        }
        // [1.2.12.2.1.4] 当該搭乗者別申込状況マップ.機内食種別=”ancillary”(有料機内食)の場合、当該搭乗者別申込状況マップ.SSRコードを引数として、「当画面共通処理No3.有料機内食名称取得処理」を行い、返却値の有料機内食名称を表示する。
        if (this.travelerStatusOfMeal.mealType === TravelerMealInfo.MealType.ANCILLARY) {
          return this._flightMealService.onHandleCommonNo3(this.travelerStatusOfMeal?.ssrCode ?? '');
        }
        // [1.2.12.2.1.5] 当該搭乗者別申込状況マップ.機内食種別=”special”(特別機内食)の場合、当該搭乗者別申込状況マップ.SSRコードを引数として、「当画面共通処理No4.特別機内食名称取得処理」を行い、返却値の特別機内食名称を表示する。"
        if (this.travelerStatusOfMeal.mealType === TravelerMealInfo.MealType.SPECIAL) {
          return this._flightMealService.onHandleCommonNo4(this.travelerStatusOfMeal?.ssrCode ?? '');
        }
      }
      // [1.2.12.2.2] 当該申込済情報が存在する場合、以下のケースのうち 1 つを表示
      else if (this.appliedMeal) {
        // [1.2.12.2.2.1] 当該申込済情報.code=""NTML""の場合、機内食を辞退している旨を表示する。
        if (this.appliedMeal.code === 'NTML') {
          labelInFlightMealName = 'label.decliningInflightMeals';
          return this._staticMsgPipe.transform(labelInFlightMealName);
        }

        // [1.2.12.2.3.2] 当該申込済情報.type=”ancillary”(有料機内食)の場合、当該搭乗者別申込状況マップ.SSRコードを引数として、「当画面共通処理No3.有料機内食名称取得処理」を行い、返却値の有料機内食名称を表示する。
        if (this.appliedMeal?.type === TravelerMealInfo.MealType.ANCILLARY) {
          return this._flightMealService.onHandleCommonNo3(this.appliedMeal?.code ?? '');
        }
        // [1.2.12.2.3.3] 当該申込済情報.type=”special”(特別機内食)の場合、当該搭乗者別申込状況マップ.SSRコードを引数として、「当画面共通処理No4.特別機内食名称取得処理」を行い、返却値の特別機内食名称を表示する。
        if (this.appliedMeal?.type === TravelerMealInfo.MealType.SPECIAL) {
          return this._flightMealService.onHandleCommonNo4(this.appliedMeal?.code ?? '');
        }
        // [1.2.12.2.3.4] 当該申込済情報.type=”preorder”(事前ミール)かつ、当該申込済情報.preOrderMealMessageIDが存在する場合、当該申込済情報.preOrderMealMessageIDに応じた事前オーダーミール名称
        if (this.appliedMeal?.type === TravelerMealInfo.MealType.PREORDER && this.appliedMeal?.preOrderMealMessageID) {
          labelInFlightMealName = this.appliedMeal?.preOrderMealMessageID;
        }
        // [1.2.12.2.3.5] 当該申込済情報.type=”preorder”(事前ミール)かつ、当該申込済情報.preOrderMealMessageIDが存在しない場合、メニュー名称が不明の旨を表示する。
        if (this.appliedMeal?.type === TravelerMealInfo.MealType.PREORDER && !this.appliedMeal?.preOrderMealMessageID) {
          labelInFlightMealName = 'label.unknownMenu';
          return this._staticMsgPipe.transform(labelInFlightMealName);
        }
      }
      // [1.2.12.2.3] 当該申込済情報が存在しない場合、以下のケースのうち 1 つを表示
      else if (this.isNotAppliedMeal) {
        labelInFlightMealName = this.getInFlightMealNameIfNotExistApplyMeal();
      }
    }

    this.labelInFlightMealName = labelInFlightMealName;
    if (isStringEmpty(labelInFlightMealName)) {
      return labelInFlightMealName;
    }

    return this._staticMsgPipe.transform(labelInFlightMealName);
  }

  /** [1.2.12.2.3] 当該申込済情報が存在しない場合: In flight meal name */
  getInFlightMealNameIfNotExistApplyMeal() {
    // [1.2.12.2.3.1] 当該セグメント別機内食情報.isWithinApplicationDeadline=trueの場合、以下のケースのうち 1 つを表示
    if (this.getMealByFlight?.isWithinApplicationDeadline) {
      if (
        this.traveler?.passengerTypeCode !== PassengerType.INS &&
        !isEmptyArray(this.getMealByFlight?.preorderMealList)
      ) {
        // [1.2.12.2.3.1.1] 当該搭乗者情報.passengerTypeCode≠""INS""(席あり幼児)、かつ 当該セグメント別機内食情報.preorderMealListが存在する場合、機内食の事前リクエストをしない旨を表示する。
        return 'label.normalMeal';
      } else {
        // [1.2.12.2.3.1.2] 上記以外の場合、通常機内食の旨を表示する。
        return 'label.in-flightMeal';
      }
    }
    // [1.2.12.2.3.2] 当該セグメント別機内食情報.isWithinApplicationDeadline=falseの場合、受付終了である旨を表示する。
    return 'label.receptionClosed';
  }

  /** [1.2.9] ただし、以下いずれかに該当する場合は押下不可とする。*/
  inFlightMealStatus(): boolean {
    /** 申込済機内食情報 */
    const inFlightMealInforApply =
      (((this.pnr?.services?.meal as Meal)[this.flight.id!] as FlightMealInfo)[
        this.traveler.id!
      ] as TravelerMealInfo) ?? {};
    let appliedMealList = inFlightMealInforApply.appliedMealList ?? [];

    // 1.当該セグメント情報.isJapanDomesticFlight=true(日本国内線)の場合
    if (this.flight.isJapanDomesticFlight) {
      return true;
    }
    // 2.以下条件を全て満たす場合
    if (
      // 2.1.当該搭乗者情報.accompanyingTravelerIdが存在しない
      isStringEmpty(this.traveler?.accompanyingTravelerId) &&
      // 2.2.申込済機内食情報.appliedMealList ≧ 2件
      appliedMealList?.length >= 2
    ) {
      return true;
    }

    // 3.以下条件を全て満たす場合
    let mealNotBBML = inFlightMealInforApply?.appliedMealList?.reduce((result: number, current: AppliedMealList) => {
      if (current.code !== TravelerMealInfo.MealCode.BBML) {
        result += 1;
      }

      return result;
    }, 0);
    if (
      // 3.1.当該搭乗者情報.accompanyingTravelerIdが存在する
      !isStringEmpty(this.traveler?.accompanyingTravelerId) &&
      // 3.2.申込済機内食情報.appliedMealListの中に、SSRコード≠""BBML""(ベビーミール)であるレコードの件数 ≧ 2件
      mealNotBBML >= 2
    ) {
      return true;
    }

    // 4.申込済機内食情報.appliedMealListの要素(以降、申込済情報)数分繰り返し、当該申込済情報.codeを引数として、「当画面共通処理.Web取扱不可取得処理」を行い、返却値のWeb取扱フラグ=""0""(不可)の場合、チェックアイコンを表示する。
    const webHandlingFlag = this._flightMealService.onHandleCommonNo2(this.appliedMeal?.code ?? '');
    if (webHandlingFlag === '0') {
      this.isDisplaySelectedMealIcon = true;
      return true;
    }

    // 5.申込済機内食情報.isWithinApplicationDeadline=false
    if (this.getMealByFlight.isWithinApplicationDeadline === false) {
      return true;
    }

    // 6.No10の申込状態にて、条件を満たす場合、当該状態を表示する。
    if (this.serviceStatus !== '') {
      this.isDisplayMealStatus = true;
      return true;
    }

    // [1.2.9.2.2] 上記以外の場合、押下可で表示する。
    // it is init value: this.unableToClickSelectMeal
    return false;
  }

  /**
   * [1.2.10] 申込状態
   * @param serviceStatus
   * @returns
   */
  setServiceStatus(serviceStatus: string) {
    let status = '';
    switch (serviceStatus) {
      // [1.2.10.1] 当該申込済情報.serviceStatus="prebooked"の場合、EMD発行待ちの旨を表示する。
      case OrderSeatItemSeatSelection.ServiceStatusEnum.Prebooked:
        status = 'label.waitingForEmd';
        break;
      // [1.2.10.2] 当該申込済情報.serviceStatus="confirmedWithoutTsm"かつ、PNR情報取得API応答.data.orderType.channelType="gds"の場合、申込窓口へ確認を促す旨を表示する。
      case OrderSeatItemSeatSelection.ServiceStatusEnum.ConfirmedWithoutTsm:
        if (this.pnr?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Gds) {
          status = 'label.appliedByOtherCarrier';
        }
        break;
      // [1.2.10.3] 当該申込済情報.serviceStatus="requesting"の場合、リクエスト中の旨を表示する。
      case OrderSeatItemSeatSelection.ServiceStatusEnum.Requesting:
        status = 'label.applying';
        break;
      // [1.2.10.4] 当該申込済情報.serviceStatus="cancelled"の場合、承れなかった旨を表示する。
      case OrderSeatItemSeatSelection.ServiceStatusEnum.Cancelled:
        status = 'label.notApplied';
        break;
      default:
        status = '';
        break;
    }
    return status;
  }

  /**
   * [1.2.12.1]当該セグメント情報.isJapanDomesticFlight=true(日本国内線)の場合、以下、利用可能なお食事・お飲み物の内容に応じた文言を表示する。
   * @param mealCodeForDomestic
   * @returns
   */
  setInFlightMealName(mealCodeForDomestic?: string) {
    switch (mealCodeForDomestic) {
      // [1.2.12.1.1] 当該セグメント別機内食情報.meals.mealCode="R"の場合、軽食が提供される旨を表示する。
      case 'R':
        return 'label.lightMeal';
      // [1.2.12.1.2] 当該セグメント別機内食情報.meals.mealCode="M"の場合、食事が提供される旨を表示する。
      case 'M':
        return 'label.meal';
      // [1.2.12.1.3] 当該セグメント別機内食情報.meals.mealCode="S"の場合、茶菓が提供される旨を表示する。
      case 'S':
        return 'label.refreshments';
    }

    return '';
  }

  onSelectMenu() {
    const part = selectMenuPayloadParts({
      travelerId: this.traveler?.id,
      flightId: this.flight?.id,
    });
    this._modal.showSubModal(part);
  }

  /**
   * (6) 削除ボタン※機内食選択モーダル
   */
  onClearMealSelection(appliedMeal: AppliedMealList) {
    // (6.1) 画面.削除ボタンに紐づく機内食種別、SSRコード、セグメントID、搭乗者IDを取得する。
    // (6.2) 最上位レベルから下位レベルまでの順次にチェックして、当該キーを基に、更新サービス情報に存在する場合、以下の通りに最下位レベルから削除する。
    // セグメントマップ\
    const hasSegmentMeal = Boolean(
      this._flightMealService.mealInformation?.segmentMap?.has(this.flight?.id!) &&
        this._flightMealService.mealInformation.segmentMap
          ?.get(this.flight?.id!)
          ?.passengerApplicationStatusMap.has(this.traveler?.id!)
    );
    if (hasSegmentMeal) {
      // 搭乗者別申込状況マップ
      this._flightMealService.mealInformation.segmentMap
        ?.get(this.flight?.id!)
        ?.passengerApplicationStatusMap.delete(this.traveler?.id!);

      if (
        this._flightMealService.mealInformation.segmentMap?.get(this.flight?.id!)?.passengerApplicationStatusMap
          .size === 0
      ) {
        this._flightMealService.mealInformation.segmentMap.delete(this.flight?.id!);
      }
    }
    // (6.3) 上記以外の場合、搭乗者ID、セグメントIDを基に、セグメントマップと搭乗者別申込状況マップをチェックして、追加する。
    else {
      this._flightMealService.mealInformation = this.updateOrInsertMealInformation(
        this.flight?.id!,
        this.traveler?.id!,
        {
          mealType: appliedMeal.type,
          ssrCode: appliedMeal.code,
          updateCategory: TravelerMealInfo.UpdateCategory.CANCEL,
        }
      );
    }
    // (6.4) 選択済み機内食を削除して、機内食メニュー選択を用意する。
    this._flightMealService._reRender.next(true);

    // (6.5)「当画面共通処理No5.申込ボタン状態処理」を行う。
    this._flightMealService.onHandleCommonNo5();
  }

  /**
   * (7) ベビーミール選択値変更処理
   */
  onChangeBabyMeal() {
    // (7.1) 画面.ベビーミール選択に紐づくセグメントID、搭乗者IDを取得する。
    // (7.2) サービシング共通store.PNR情報取得API応答.data.services.meal.<セグメントID>.<搭乗者ID>である情報を申込済機内食情報とする。
    const inFlightMealInforApply =
      (((this.pnr?.services?.meal as Meal)?.[this.flight.id!] as FlightMealInfo)?.[
        this.traveler.id!
      ] as TravelerMealInfo) ?? {};
    const isHaveBBML: boolean = inFlightMealInforApply?.appliedMealList?.some((appliedMeal: AppliedMealList) => {
      return appliedMeal.code === TravelerMealInfo.MealCode.BBML;
    });

    // (7.3) 申込済機内食情報.appliedMealListの中に、SSRコード="BBML"が存在する場合、以下の処理を行う。
    if (isHaveBBML) {
      // (7.3.1) 画面.ベビーミール選択がチェックOFFの場合、以下の情報を更新サービス情報に追加する。
      if (this.isBabyMealChecked !== true) {
        const changedData = {
          updateCategory: TravelerMealInfo.UpdateCategory.CANCEL,
          babyMealSelectionFlag: true,
        } as PassengerApplicationStatusMap;

        this._flightMealService.mealInformation = this.updateOrInsertMealInformation(
          this.flight?.id!,
          this.traveler?.id!,
          changedData
        );
      }
      // (7.3.2) 上記以外の場合、搭乗者ID、セグメントIDを基に、更新サービス情報からセグメントマップと搭乗者別申込状況マップをチェックして、削除する。
      else {
        this.deleteSegmentInMealInformation();
      }
    }
    // (7.4) 上記以外の場合、以下の処理を行う。
    else {
      // (7.4.1) 画面.ベビーミール選択がチェックONの場合、以下の情報を更新サービス情報に追加する。
      if (this.isBabyMealChecked) {
        const changedData = {
          updateCategory: TravelerMealInfo.UpdateCategory.REQUEST,
          babyMealSelectionFlag: true,
          mealType: GetMealResponseDataDDAppliedMealListInner.TypeEnum.Special,
          ssrCode: TravelerMealInfo.MealCode.BBML,
        } as PassengerApplicationStatusMap;

        this._flightMealService.mealInformation = this.updateOrInsertMealInformation(
          this.flight?.id!,
          this.traveler?.id!,
          changedData
        );
      }
      // (7.4.2) 上記以外の場合、搭乗者ID、セグメントIDを基に、更新サービス情報からセグメントマップと搭乗者別申込状況マップをチェックして、削除する。
      else {
        this.deleteSegmentInMealInformation();
      }
    }

    // (7.5) 「当画面共通処理No5.申込ボタン状態処理」を行う。
    this._flightMealService.onHandleCommonNo5();
  }

  deleteSegmentInMealInformation() {
    if (this._flightMealService.mealInformation?.segmentMap?.has(this.flight?.id!)) {
      if (
        this._flightMealService.mealInformation.segmentMap
          ?.get(this.flight?.id!)
          ?.passengerApplicationStatusMap.has(this.traveler?.id!)
      ) {
        this._flightMealService.mealInformation.segmentMap
          ?.get(this.flight?.id!)
          ?.passengerApplicationStatusMap.delete(this.traveler?.id!);
      }

      if (
        this._flightMealService.mealInformation.segmentMap?.get(this.flight?.id!)?.passengerApplicationStatusMap
          .size === 0
      ) {
        this._flightMealService.mealInformation.segmentMap.delete(this.flight?.id!);
      }
    }
  }

  updateOrInsertMealInformation(flightId: string, travelerId: string, passengerData: PassengerApplicationStatusMap) {
    const mealInformation = { ...this._flightMealService.mealInformation };
    if (!mealInformation?.segmentMap) {
      mealInformation.segmentMap = new Map<string, SegmentMap>();
    }

    if (mealInformation?.segmentMap?.has(flightId) !== true) {
      mealInformation?.segmentMap?.set(flightId, {
        passengerApplicationStatusMap: new Map<string, PassengerApplicationStatusMap>(),
        mealCatalog: [] as MealCatalogItem[],
      });
    }

    mealInformation?.segmentMap?.get(flightId)?.passengerApplicationStatusMap?.set(travelerId, passengerData);
    return mealInformation;
  }

  getMealName() {
    return (
      this._flightMealService.mealInformation.segmentMap
        ?.get(this.flight.id!)
        ?.passengerApplicationStatusMap?.get(this.traveler.id!)?.mealType ?? ''
    );
  }

  haveMealSelected(flightId: string, travelerId: string): boolean {
    return Boolean(
      this._flightMealService.mealInformation.segmentMap?.get(flightId)?.passengerApplicationStatusMap?.get(travelerId)
        ?.mealType
    );
  }

  // Save info to handle Event number 10 at 5.3
  setCanSelectAllMealByTravelers() {
    this._flightMealService.updateValueForCanSelectAllMealByTravelers(
      this.traveler?.id!,
      this.flight?.id!,
      this.unableToClickSelectMeal !== true
    );
  }

  reload(): void {}

  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }
}
